import React, { useState } from 'react';
import { Plus, BookOpen, Edit, Trash2, Star, Calendar, TrendingUp, Target } from 'lucide-react';
import { PageTracker } from '../../../components/PageTracker.tsx';

const ReviewJournal: React.FC = () => {
  const [entries, setEntries] = useState([
    {
      id: 1,
      date: "2025-08-10",
      type: "Daily",
      title: "Great Progress on Portfolio",
      content: "Completed the main landing page design. The new color scheme works well. Need to focus on the project showcase section tomorrow. Learned a lot about advanced CSS Grid techniques.",
      mood: "Productive",
      rating: 8,
      tags: ["Portfolio", "CSS", "Design"],
      goals: ["Complete landing page ✓", "Learn CSS Grid ✓", "Plan project section"]
    },
    {
      id: 2,
      date: "2025-08-09",
      type: "Weekly",
      title: "Week 32 Review",
      content: "This week was focused on learning React advanced patterns. Completed 3 out of 4 planned modules. The Context API section was particularly challenging but rewarding. Need to allocate more time for hands-on practice.",
      mood: "Reflective",
      rating: 7,
      tags: ["React", "Learning", "Context API"],
      goals: ["Complete React course", "Build practice project", "Review documentation"]
    },
    {
      id: 3,
      date: "2025-08-03",
      type: "Monthly",
      title: "July Retrospective",
      content: "July was a month of significant growth. Completed 2 major projects, learned TypeScript, and improved my design skills. The biggest challenge was time management between learning and building. Next month, I want to focus more on contributing to open source projects.",
      mood: "Accomplished",
      rating: 9,
      tags: ["TypeScript", "Projects", "Growth"],
      goals: ["Open source contributions", "Improve time management", "Advanced TypeScript"]
    }
  ]);

  const [filterType, setFilterType] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const entryTypes = ['All', 'Daily', 'Weekly', 'Monthly', 'Project'];

  const filteredEntries = entries.filter(entry => {
    const matchesType = filterType === 'All' || entry.type === filterType;
    const matchesSearch = entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesType && matchesSearch;
  });

  const getMoodColor = (mood: string) => {
    switch (mood.toLowerCase()) {
      case 'productive': return 'bg-green-100 text-green-800';
      case 'reflective': return 'bg-blue-100 text-blue-800';
      case 'accomplished': return 'bg-purple-100 text-purple-800';
      case 'challenging': return 'bg-orange-100 text-orange-800';
      case 'frustrated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 8) return 'text-green-600';
    if (rating >= 6) return 'text-yellow-600';
    if (rating >= 4) return 'text-orange-600';
    return 'text-red-600';
  };

  const addEntry = () => {
    // This would open a form to add a new review entry
    console.log('Add review entry form would open here');
  };

  return (
    <div className="p-6 max-w-7xl mx-auto project-mgmt-page">
      <PageTracker pageTitle="Review Journal" category="project-management" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Review Journal</h1>
        <p className="text-gray-600 text-lg">
          Reflect on your progress, capture insights, and track your growth journey. Regular reviews help 
          identify patterns, celebrate achievements, and plan for continuous improvement.
        </p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <button 
            onClick={addEntry}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700"
          >
            <Plus size={20} />
            <span>New Entry</span>
          </button>
          
          <input
            type="text"
            placeholder="Search entries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <select 
          value={filterType} 
          onChange={(e) => setFilterType(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2"
        >
          {entryTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100">Total Entries</p>
              <p className="text-3xl font-bold">{entries.length}</p>
            </div>
            <BookOpen size={32} className="text-blue-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100">Avg. Rating</p>
              <p className="text-3xl font-bold">
                {(entries.reduce((sum, entry) => sum + entry.rating, 0) / entries.length).toFixed(1)}
              </p>
            </div>
            <Star size={32} className="text-green-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100">This Month</p>
              <p className="text-3xl font-bold">
                {entries.filter(entry => {
                  const entryMonth = new Date(entry.date).getMonth();
                  const currentMonth = new Date().getMonth();
                  return entryMonth === currentMonth;
                }).length}
              </p>
            </div>
            <Calendar size={32} className="text-purple-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100">Growth Trend</p>
              <p className="text-2xl font-bold flex items-center">
                +12%
                <TrendingUp size={24} className="ml-2 text-orange-200" />
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {filteredEntries.map((entry) => (
            <div key={entry.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <BookOpen className="text-blue-600" size={20} />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{entry.title}</h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <span>{entry.type} Review</span>
                      <span>•</span>
                      <span>{new Date(entry.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="flex items-center space-x-1">
                    <Star className={`${getRatingColor(entry.rating)}`} size={16} />
                    <span className={`font-medium ${getRatingColor(entry.rating)}`}>
                      {entry.rating}/10
                    </span>
                  </div>
                  <button className="text-gray-400 hover:text-blue-600">
                    <Edit size={16} />
                  </button>
                  <button className="text-gray-400 hover:text-red-600">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getMoodColor(entry.mood)}`}>
                  {entry.mood}
                </span>
              </div>
              
              <p className="text-gray-700 mb-4 leading-relaxed">{entry.content}</p>
              
              {entry.goals && entry.goals.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Goals & Actions:</h4>
                  <ul className="space-y-1">
                    {entry.goals.map((goal, index) => (
                      <li key={index} className="text-sm text-gray-600 flex items-center">
                        <Target size={12} className="mr-2 text-gray-400" />
                        {goal}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="flex flex-wrap gap-1">
                {entry.tags.map((tag, index) => (
                  <span key={index} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
          
          {filteredEntries.length === 0 && (
            <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-gray-500">
              <BookOpen size={48} className="mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium mb-2">No entries found</h3>
              <p className="text-sm">Start your reflection journey by adding your first review entry!</p>
            </div>
          )}
        </div>
        
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Schedule</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Daily Review</span>
                <span className="text-sm text-green-600 font-medium">On track</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Weekly Review</span>
                <span className="text-sm text-blue-600 font-medium">Due Sun</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Monthly Review</span>
                <span className="text-sm text-orange-600 font-medium">Due 31st</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Quarterly Review</span>
                <span className="text-sm text-purple-600 font-medium">Due Sep 30</span>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Insights</h3>
            <div className="space-y-3 text-sm">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-blue-800">You're most productive on Tuesday mornings</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-green-800">Learning goals are consistently met</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800">Consider more time for reflection</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-indigo-900 mb-2">Review Tips</h3>
            <ul className="text-sm text-indigo-800 space-y-1">
              <li>• Be honest about both successes and challenges</li>
              <li>• Focus on learning and growth, not perfection</li>
              <li>• Set specific, actionable goals for next period</li>
              <li>• Track patterns and recurring themes</li>
              <li>• Celebrate small wins and progress</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewJournal;
